import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRegionService } from '@/composables/services/useRegionService'
import type { Region, GetRegionsParams } from '@/domain/entities/region.entity'
import type { PaginatedResult } from '@/domain/entities/base.entity'
import { ErrorHandler } from '@/infrastructure/errors'
import { useErrors } from '../composables/app/useErrors'

/**
 * Store для роботи з регіонами.
 * Використовує Vue 3 Composition API (setup синтаксис).
 */
export const useRegionStore = defineStore('region', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('region')
  // ========== State ==========
  const regions = ref<Region[]>([])
  const pagination = ref<PaginatedResult<Region> | null>(null)
  const loading = ref(false)
  /** Параметри останнього запиту списку (для loadMore) */
  const lastFetchParams = ref<GetRegionsParams | null>(null)

  // ========== Service ==========
  const regionService = useRegionService()

  // ========== Getters (Computed) ==========
  const hasRegions = computed(() => regions.value.length > 0)
  const isLoading = computed(() => loading.value)

  // ========== Actions ==========

  /**
   * Отримати список регіонів з пагінацією та фільтрами.
   * @param params - Параметри запиту (regionIso, search, cursor, limit)
   * @param append - Якщо true, додає до існуючого списку (loadMore)
   */
  async function fetchRegions(params?: GetRegionsParams, append = false) {
    loading.value = true
    clearErrors()

    try {
      const result = await regionService.getRegions(params)

      if (append) {
        regions.value = [...regions.value, ...result.items]
      } else {
        regions.value = result.items
      }

      pagination.value = {
        items: regions.value,
        cursor: result.cursor,
        limit: result.limit,
        hasNextPage: result.hasNextPage,
      }
      lastFetchParams.value = params ?? null

      return result
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      throw handledError
    } finally {
      loading.value = false
    }
  }

  /**
   * Завантажити наступну сторінку регіонів.
   */
  async function loadMore() {
    const cursor = pagination.value?.cursor
    if (!cursor || !lastFetchParams.value) return

    return fetchRegions(
      {
        ...lastFetchParams.value,
        cursor,
        limit: lastFetchParams.value.limit ?? pagination.value?.limit ?? 50,
      },
      true
    )
  }

  /**
   * Скинути стан store.
   */
  function reset() {
    regions.value = []
    pagination.value = null
    loading.value = false
    clearErrors()
    lastFetchParams.value = null
  }

  /**
   * Очистити помилку.
   */
  function clearError() {
    clearErrors()
  }

  // ========== Return ==========
  return {
    // State
    regions,
    pagination,
    loading: isLoading,
    error,

    // Getters
    hasRegions,
    hasError,

    // Actions
    fetchRegions,
    loadMore,
    reset,
    clearError,
  }
})
